import React from 'react';
import { Result } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import { Button } from '../Button';
import { useUIText } from '../../i18n';
import type { Styleable } from '../../variants';

export interface ErrorStateProps extends Styleable {
    title?: React.ReactNode;
    description?: React.ReactNode;
    onRetry?: () => void;
    retrying?: boolean;
    icon?: React.ReactNode;
}

/**
 * Tier-1 presentational primitive: a domain-free "something went wrong" panel
 * for a failed fetch/action. Built on antd's `Result` rather than `Empty` —
 * `Result` is purpose-built for status pages (icon + title + subtitle +
 * action area), which matches this component's title/description/retry
 * layout directly, whereas `Empty` only offers a single description line and
 * would need extra markup bolted on to reach the same shape. Unset `title` /
 * retry label fall back to `common.errorTitle` / `common.retry` from the
 * shared i18n catalog; callers can override either via props.
 */
export function ErrorState({
    title,
    description,
    onRetry,
    retrying = false,
    icon,
    className,
    style,
}: ErrorStateProps): React.ReactElement {
    const text = useUIText();

    return (
        <div role="alert" aria-live="assertive" className={className} style={style}>
            <Result
                status="error"
                icon={icon}
                title={title ?? text.common.errorTitle}
                subTitle={description}
                extra={
                    onRetry ? (
                        <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            loading={retrying}
                            onClick={onRetry}
                        >
                            {text.common.retry}
                        </Button>
                    ) : undefined
                }
            />
        </div>
    );
}

export default ErrorState;
